import type { ApplicationId } from '@/types';
import type { ApplicationCategory, ApplicationManifest } from './types';

/**
 * Applications register once at boot. The registry is a plain module so it can be
 * consulted by non-React code (commands, persistence, the launcher's search) without
 * a component tree.
 */
export class ApplicationRegistry {
  private readonly apps = new Map<ApplicationId, ApplicationManifest>();

  register(manifest: ApplicationManifest): void {
    if (this.apps.has(manifest.id)) throw new Error(`Application "${manifest.id}" is already registered`);
    if (!/^\d+\.\d+\.\d+$/.test(manifest.version))
      throw new Error(`Application "${manifest.id}" has a non-semver version`);
    this.apps.set(manifest.id, manifest);
  }

  get(id: ApplicationId): ApplicationManifest | undefined {
    return this.apps.get(id);
  }

  has(id: ApplicationId): boolean {
    return this.apps.has(id);
  }

  list(): readonly ApplicationManifest[] {
    return [...this.apps.values()];
  }

  byCategory(category: ApplicationCategory): readonly ApplicationManifest[] {
    return this.list().filter((m) => m.category === category);
  }

  /** Case-insensitive match on name, category and description; empty query returns everything. */
  search(query: string): readonly ApplicationManifest[] {
    const q = query.trim().toLowerCase();
    if (!q) return this.list();
    const score = (m: ApplicationManifest): number => {
      const name = m.name.toLowerCase();
      if (name === q) return 0;
      if (name.startsWith(q)) return 1;
      if (name.includes(q)) return 2;
      if (m.category.includes(q)) return 3;
      if (m.description.toLowerCase().includes(q)) return 4;
      return -1;
    };
    return this.list()
      .map((m) => ({ m, s: score(m) }))
      .filter((x) => x.s >= 0)
      .sort((a, b) => a.s - b.s || a.m.name.localeCompare(b.m.name))
      .map((x) => x.m);
  }
}
